import { useState, useEffect, useCallback } from "react";
import {
  Mail,
  Send,
  Eye,
  MousePointerClick,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  RefreshCw,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  RotateCcw,
} from "lucide-react";
import api from "@/lib/api-client";
import { useGlobalToast } from "../../contexts/ToastContext";

interface EmailLog {
  id: string;
  organizationId: string | null;
  recipientEmail: string;
  recipientName: string | null;
  subject: string;
  type: string;
  status: string;
  messageId: string | null;
  metadata: Record<string, unknown> | null;
  openedAt: string | null;
  openCount: number;
  clickedAt: string | null;
  clickCount: number;
  failedReason: string | null;
  createdAt: string;
  organization: { id: string; name: string; slug: string } | null;
}

interface EmailStats {
  totalSent: number;
  opened: number;
  clicked: number;
  failed: number;
  openRate: number;
  clickRate: number;
  byType: Array<{ type: string; count: number }>;
  byDay: Array<{ date: string; count: number }>;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const EMAIL_TYPE_LABELS: Record<string, string> = {
  reservation_confirmation: "Confirmación de reserva",
  new_reservation_admin: "Nueva reserva (admin)",
  welcome: "Bienvenida",
  invitation: "Invitación equipo",
  custom: "Personalizado",
  admin_custom: "Email admin",
  email_verification: "Verificación email",
  password_reset: "Reset contraseña",
  trial_expiring: "Trial por expirar",
  trial_ended: "Trial finalizado",
  free_user_upgrade: "Upgrade plan gratuito",
  inactive_user_reengagement: "Reactivación usuario",
  onboarding_day2: "Onboarding día 2",
  first_reservation_celebration: "Primera reserva",
  subscription_confirmation: "Confirmación suscripción",
  subscription_cancelled: "Suscripción cancelada",
  payment_failed: "Pago fallido",
  stripe_checkout_notification: "Pago Stripe (admin)",
  plan_change_notification: "Cambio de plan (admin)",
  new_registration_notification: "Nuevo registro (admin)",
  support_ticket_new: "Ticket soporte nuevo",
  support_ticket_reply: "Respuesta ticket",
  support_ticket_user_reply: "Respuesta usuario ticket",
  support_ticket_escalated: "Ticket escalado",
  email_change_verification: "Verificación cambio email",
  email_change_notification: "Notificación cambio email",
  contact_form: "Formulario contacto",
};

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; icon: typeof Mail }
> = {
  sent: {
    label: "Enviado",
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    icon: Send,
  },
  delivered: {
    label: "Entregado",
    color: "text-green-700",
    bgColor: "bg-green-100",
    icon: CheckCircle,
  },
  opened: {
    label: "Abierto",
    color: "text-purple-700",
    bgColor: "bg-purple-100",
    icon: Eye,
  },
  clicked: {
    label: "Clic",
    color: "text-indigo-700",
    bgColor: "bg-indigo-100",
    icon: MousePointerClick,
  },
  failed: {
    label: "Fallido",
    color: "text-red-700",
    bgColor: "bg-red-100",
    icon: XCircle,
  },
  bounced: {
    label: "Rebotado",
    color: "text-orange-700",
    bgColor: "bg-orange-100",
    icon: AlertCircle,
  },
};

export default function AdminEmails() {
  const [emails, setEmails] = useState<EmailLog[]>([]);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
  const [detailTab, setDetailTab] = useState<"info" | "preview">("info");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const handleOpenDetail = (email: EmailLog) => {
    setSelectedEmail(email);
    setDetailTab("info");
    setPreviewHtml(null);
    setPreviewError(false);
  };

  const handlePreviewTab = async (emailId: string) => {
    setDetailTab("preview");
    if (previewHtml !== null || previewLoading) return;
    setPreviewLoading(true);
    setPreviewError(false);
    try {
      const res = await api.get(`/admin/emails/${emailId}/preview`, {
        responseType: "text",
      });
      setPreviewHtml(res.data as string);
    } catch {
      setPreviewError(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({
    recipientEmail: "",
    recipientName: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const { showToast } = useGlobalToast();

  const fetchEmails = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const params: Record<string, string> = {
          page: String(page),
          limit: "50",
        };
        if (search) params.search = search;
        if (typeFilter !== "all") params.type = typeFilter;
        if (statusFilter !== "all") params.status = statusFilter;

        const res = await api.get("/admin/emails", { params });
        setEmails(res.data.data);
        setPagination(res.data.pagination);
      } catch (error) {
        console.error("Error fetching emails:", error);
      } finally {
        setLoading(false);
      }
    },
    [search, typeFilter, statusFilter],
  );

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/emails/stats");
      setStats(res.data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchEmails();
    fetchStats();
  }, [fetchEmails, fetchStats]);

  const handleSendEmail = async () => {
    if (!sendForm.recipientEmail || !sendForm.subject || !sendForm.message)
      return;
    try {
      setSending(true);
      await api.post("/admin/emails/send", sendForm);
      setShowSendModal(false);
      setSendForm({
        recipientEmail: "",
        recipientName: "",
        subject: "",
        message: "",
      });
      fetchEmails();
      fetchStats();
    } catch (error) {
      console.error("Error sending email:", error);
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (emailId: string) => {
    try {
      setResendingId(emailId);
      const res = await api.post(`/admin/emails/${emailId}/resend`);
      if (res.data?.success) {
        showToast(
          res.data.message || "Email reenviado correctamente",
          "success",
        );
        setSelectedEmail(null);
      } else {
        showToast(res.data?.message || "No se pudo reenviar el email", "error");
      }
      await Promise.all([fetchEmails(pagination.page), fetchStats()]);
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Error al reenviar el email";
      showToast(message, "error");
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.sent;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getTypeLabel = (type: string) => EMAIL_TYPE_LABELS[type] || type;

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Emails
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Monitoriza todos los emails enviados desde la plataforma
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              fetchEmails();
              fetchStats();
            }}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowSendModal(true)}
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            <Send className="w-4 h-4" />
            Enviar Email
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Send className="w-4 h-4" />
              <span className="text-xs font-medium">Enviados</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {stats.totalSent}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Eye className="w-4 h-4" />
              <span className="text-xs font-medium">Abiertos</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{stats.opened}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {stats.openRate}% tasa apertura
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <MousePointerClick className="w-4 h-4" />
              <span className="text-xs font-medium">Clics</span>
            </div>
            <p className="text-2xl font-bold text-indigo-600">
              {stats.clicked}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {stats.clickRate}% tasa clics
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <XCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Fallidos</span>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <BarChart3 className="w-4 h-4" />
              <span className="text-xs font-medium">Tipos</span>
            </div>
            <div className="space-y-1 mt-1">
              {stats.byType.slice(0, 3).map((t) => (
                <div
                  key={t.type}
                  className="flex items-center justify-between text-xs"
                >
                  <span className="text-gray-600 truncate">
                    {getTypeLabel(t.type)}
                  </span>
                  <span className="font-medium text-gray-900 ml-2">
                    {t.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="bg-white rounded-xl border border-gray-200 mb-4">
        <div className="p-3 md:p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por email, nombre o asunto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchEmails(1)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
              showFilters
                ? "border-primary-500 bg-primary-50 text-primary-700"
                : "border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}
          >
            <Filter className="w-4 h-4" />
            Filtros
          </button>
          <button
            onClick={() => fetchEmails(1)}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800"
          >
            Buscar
          </button>
        </div>
        {showFilters && (
          <div className="px-3 md:px-4 pb-3 md:pb-4 flex flex-col sm:flex-row gap-3 border-t border-gray-100 pt-3">
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Todos los tipos</option>
              {Object.entries(EMAIL_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">Todos los estados</option>
              {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                <option key={value} value={value}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Email List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : emails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Mail className="w-12 h-12 mb-3" />
            <p className="text-sm">No se encontraron emails</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {emails.map((email) => (
                <div
                  key={email.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleOpenDetail(email)}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {email.recipientName || email.recipientEmail}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {email.recipientEmail}
                      </p>
                    </div>
                    {getStatusBadge(email.status)}
                  </div>
                  <p className="text-sm text-gray-700 truncate mb-1">
                    {email.subject}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100">
                      {getTypeLabel(email.type)}
                    </span>
                    <span>{formatDate(email.createdAt)}</span>
                    {email.openCount > 0 && (
                      <span className="text-purple-600 font-medium">
                        <Eye className="w-3 h-3 inline" /> {email.openCount}
                      </span>
                    )}
                    {email.clickCount > 0 && (
                      <span className="text-indigo-600 font-medium">
                        <MousePointerClick className="w-3 h-3 inline" />{" "}
                        {email.clickCount}
                      </span>
                    )}
                  </div>
                  {(email.status === "failed" || email.status === "bounced") && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResend(email.id);
                      }}
                      disabled={resendingId === email.id}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-primary-200 text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                    >
                      {resendingId === email.id ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3.5 h-3.5" />
                      )}
                      {resendingId === email.id
                        ? "Reenviando..."
                        : "Reenviar"}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Destinatario
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Asunto
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Tipo
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Estado
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Aperturas
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Clics
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Fecha
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                      Organización
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {emails.map((email) => (
                    <tr
                      key={email.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleOpenDetail(email)}
                    >
                      <td className="px-4 py-3">
                        <div className="max-w-[200px]">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {email.recipientName || "-"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {email.recipientEmail}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700 truncate max-w-[250px]">
                          {email.subject}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 whitespace-nowrap">
                          {getTypeLabel(email.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(email.status)}
                          {(email.status === "failed" || email.status === "bounced") && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResend(email.id);
                              }}
                              disabled={resendingId === email.id}
                              title="Reenviar email"
                              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md border border-primary-200 text-primary-700 hover:bg-primary-50 disabled:opacity-50 whitespace-nowrap"
                            >
                              {resendingId === email.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <RotateCcw className="w-3 h-3" />
                              )}
                              Reenviar
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {email.openCount > 0 ? (
                          <span className="text-sm font-medium text-purple-600">
                            {email.openCount}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {email.clickCount > 0 ? (
                          <span className="text-sm font-medium text-indigo-600">
                            {email.clickCount}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(email.createdAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500 truncate max-w-[120px] block">
                          {email.organization?.name || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              {pagination.total} emails total · Página {pagination.page} de{" "}
              {pagination.totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchEmails(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => fetchEmails(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Email Detail Modal */}
      {selectedEmail && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSelectedEmail(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-h-[90dvh] flex flex-col"
            style={{ maxWidth: detailTab === "preview" ? "860px" : "512px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Detalle del Email
                </h3>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                  <button
                    onClick={() => setDetailTab("info")}
                    className={`px-3 py-1 font-medium ${
                      detailTab === "info"
                        ? "bg-primary-600 text-white"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    Detalles
                  </button>
                  <button
                    onClick={() => handlePreviewTab(selectedEmail.id)}
                    className={`px-3 py-1 font-medium ${
                      detailTab === "preview"
                        ? "bg-primary-600 text-white"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    Preview
                  </button>
                </div>
              </div>
              <button
                onClick={() => setSelectedEmail(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Tab: Preview */}
            {detailTab === "preview" && (
              <div className="flex-1 overflow-hidden">
                {previewLoading && (
                  <div className="flex items-center justify-center h-64">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                )}
                {previewError && (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-400 gap-3">
                    <Mail className="w-10 h-10" />
                    <p className="text-sm text-center">
                      Preview no disponible.
                      <br />
                      <span className="text-xs text-gray-400">
                        Solo emails enviados tras la actualización tienen
                        preview.
                      </span>
                    </p>
                  </div>
                )}
                {previewHtml && (
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full border-0"
                    style={{ height: "70vh" }}
                    sandbox="allow-same-origin"
                    title="Preview del email"
                  />
                )}
              </div>
            )}

            {/* Tab: Detalles */}
            {detailTab === "info" && (
              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Destinatario
                    </label>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {selectedEmail.recipientName || "-"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedEmail.recipientEmail}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Estado
                    </label>
                    <div className="mt-1">
                      {getStatusBadge(selectedEmail.status)}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    Asunto
                  </label>
                  <p className="text-sm text-gray-900 mt-0.5">
                    {selectedEmail.subject}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Tipo
                    </label>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {getTypeLabel(selectedEmail.type)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Organización
                    </label>
                    <p className="text-sm text-gray-700 mt-0.5">
                      {selectedEmail.organization?.name || "Sin organización"}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-gray-500 mb-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="text-xs">Enviado</span>
                    </div>
                    <p className="text-xs font-medium text-gray-700">
                      {formatDate(selectedEmail.createdAt)}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-purple-500 mb-1">
                      <Eye className="w-3.5 h-3.5" />
                      <span className="text-xs">Aperturas</span>
                    </div>
                    <p className="text-lg font-bold text-purple-700">
                      {selectedEmail.openCount}
                    </p>
                    {selectedEmail.openedAt && (
                      <p className="text-xs text-purple-500 mt-0.5">
                        Primera: {formatDate(selectedEmail.openedAt)}
                      </p>
                    )}
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-indigo-500 mb-1">
                      <MousePointerClick className="w-3.5 h-3.5" />
                      <span className="text-xs">Clics</span>
                    </div>
                    <p className="text-lg font-bold text-indigo-700">
                      {selectedEmail.clickCount}
                    </p>
                    {selectedEmail.clickedAt && (
                      <p className="text-xs text-indigo-500 mt-0.5">
                        Primer: {formatDate(selectedEmail.clickedAt)}
                      </p>
                    )}
                  </div>
                </div>

                {selectedEmail.failedReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-red-700 mb-0.5">
                      Error
                    </p>
                    <p className="text-sm text-red-600">
                      {selectedEmail.failedReason}
                    </p>
                  </div>
                )}

                {selectedEmail.metadata &&
                  Object.keys(selectedEmail.metadata).length > 0 && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase">
                        Metadata
                      </label>
                      <pre className="text-xs bg-gray-50 rounded-lg p-3 mt-1 overflow-x-auto text-gray-600">
                        {JSON.stringify(selectedEmail.metadata, null, 2)}
                      </pre>
                    </div>
                  )}

                {selectedEmail.messageId && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      Message ID
                    </label>
                    <p className="text-xs text-gray-500 font-mono mt-0.5 break-all">
                      {selectedEmail.messageId}
                    </p>
                  </div>
                )}
              </div>
            )}

            {(selectedEmail.status === "failed" ||
              selectedEmail.status === "bounced") && (
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                <p className="text-xs text-gray-500">
                  Se reenviará a {selectedEmail.recipientEmail}
                </p>
                <button
                  onClick={() => handleResend(selectedEmail.id)}
                  disabled={resendingId === selectedEmail.id}
                  className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {resendingId === selectedEmail.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  {resendingId === selectedEmail.id
                    ? "Reenviando..."
                    : "Reenviar email"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showSendModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowSendModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Enviar Email
              </h3>
              <button
                onClick={() => setShowSendModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={sendForm.recipientEmail}
                    onChange={(e) =>
                      setSendForm({
                        ...sendForm,
                        recipientEmail: e.target.value,
                      })
                    }
                    placeholder="correo@ejemplo.com"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    value={sendForm.recipientName}
                    onChange={(e) =>
                      setSendForm({
                        ...sendForm,
                        recipientName: e.target.value,
                      })
                    }
                    placeholder="Nombre del destinatario"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto *
                </label>
                <input
                  type="text"
                  value={sendForm.subject}
                  onChange={(e) =>
                    setSendForm({ ...sendForm, subject: e.target.value })
                  }
                  placeholder="Asunto del email"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensaje *
                </label>
                <textarea
                  value={sendForm.message}
                  onChange={(e) =>
                    setSendForm({ ...sendForm, message: e.target.value })
                  }
                  placeholder="Escribe el contenido del email..."
                  rows={6}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowSendModal(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendEmail}
                  disabled={
                    sending ||
                    !sendForm.recipientEmail ||
                    !sendForm.subject ||
                    !sendForm.message
                  }
                  className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {sending ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {sending ? "Enviando..." : "Enviar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
